/**
 * PeriodReopenCenter — Phase 8 · Four-Eyes workflow surface (Feb 2026).
 *
 * Manages the full lifecycle of period_reopen_requests:
 *   • Request a CLOSED period to be reopened (with reason + duration)
 *   • Approve / reject pending requests (Four-Eyes — the requester
 *     cannot approve their own row; the server enforces this too)
 *   • Close active windows early (freezes the Impact Report)
 *   • Read the full Reopen Impact Report after close-again / expire
 *
 * All copy is in English with key terms mirrored in Arabic to match
 * the rest of the ERP. The page is admin-only at the route level.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  ShieldAlert, ClipboardList, Clock, CheckCircle2, XCircle,
  RefreshCw, Lock, Unlock, FileText, AlertTriangle, Users,
} from 'lucide-react';

const REOPEN_TYPES = [
  { id: 'adjustment_entry', label: 'Adjustment Entry Only' },
  { id: 'missing_invoice',  label: 'Missing Invoice' },
  { id: 'missing_expense',  label: 'Missing Expense' },
  { id: 'audit_correction', label: 'Audit Correction' },
  { id: 'other',            label: 'Other' },
];

const STATUS_BADGE = {
  pending:      'bg-amber-100 text-amber-800 border-amber-300',
  approved:     'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected:     'bg-rose-100 text-rose-800 border-rose-300',
  expired:      'bg-slate-200 text-slate-700 border-slate-300',
  closed_again: 'bg-slate-900 text-white border-slate-900',
};

const SAR = (n) => (Number(n) || 0).toLocaleString('en-US',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });


export default function PeriodReopenCenter({ user }) {
  const [rows, setRows]       = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail]   = useState(null);

  const isAdmin = ((user?.role || '') === 'admin');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        axiosInstance.get('/period-reopen?limit=100'),
        axiosInstance.get('/periods-mgmt'),
      ]);
      setRows(r.data || []);
      // /periods-mgmt returns { periods: [...] }; tolerate either shape.
      const periodsList = Array.isArray(p.data)
        ? p.data
        : (p.data?.periods || []);
      setPeriods(periodsList.filter((x) => (x.status || '').toLowerCase() === 'closed'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load reopen requests');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (!isAdmin) {
    return (
      <div className="p-8 text-sm text-slate-500 text-center">
        Admin access required.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="period-reopen-center">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldAlert size={22} className="text-rose-600" />
            Period Reopen Center
            <span className="text-xs font-normal text-slate-400 mr-2">
              · إعادة فتح الفترات المغلقة
            </span>
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Four-Eyes-protected workflow for reopening CLOSED accounting
            periods. Requester ≠ Approver. Every window auto-builds an
            immutable Impact Report at close.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="reopen-refresh-btn"
            onClick={loadAll}
            className="inline-flex items-center gap-1 text-sm border border-slate-300 px-3 py-1.5 rounded-md hover:bg-slate-50"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            data-testid="reopen-request-btn"
            onClick={() => setShowCreate(true)}
            disabled={periods.length === 0}
            className="inline-flex items-center gap-1 text-sm bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-md disabled:opacity-40"
          >
            <Unlock size={14} /> Request Reopen
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="border border-dashed border-slate-200 rounded-lg py-12 text-center text-sm text-slate-400">
          <ClipboardList size={28} className="mx-auto mb-2 opacity-50" />
          No reopen requests yet.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="reopen-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Period</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Requested By</th>
                <th className="text-left px-4 py-2">Approved By</th>
                <th className="text-right px-4 py-2">Duration</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Affected</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.id} r={r} user={user}
                     onChanged={loadAll}
                     onDetail={() => setDetail(r)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateModal
          periods={periods}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadAll(); }}
        />
      )}

      {detail && (
        <DetailModal row={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// Table row + per-row actions
// ───────────────────────────────────────────────────────────────────────
function Row({ r, user, onChanged, onDetail }) {
  const [busy, setBusy] = useState(false);
  const isOwnRow = r.requested_by === user?.id;

  const remaining = (() => {
    if (r.status !== 'approved' || !r.expires_at) return null;
    const ms = new Date(r.expires_at).getTime() - Date.now();
    if (ms <= 0) return 'expiring…';
    const m = Math.floor(ms / 60_000);
    return `${m}m left`;
  })();

  const approve = async () => {
    setBusy(true);
    try {
      await axiosInstance.post(`/period-reopen/${r.id}/approve`, {});
      toast.success('Approved — window is now open');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve');
    } finally { setBusy(false); }
  };
  const reject = async () => {
    const reason = window.prompt('Reason for rejection (min 10 chars):');
    if (!reason || reason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    setBusy(true);
    try {
      await axiosInstance.post(`/period-reopen/${r.id}/reject`,
        { rejection_reason: reason.trim() });
      toast.success('Rejected');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reject');
    } finally { setBusy(false); }
  };
  const closeNow = async () => {
    if (!window.confirm('Close the reopen window now? This will freeze the Impact Report.')) return;
    setBusy(true);
    try {
      await axiosInstance.post(`/period-reopen/${r.id}/close-now`, {});
      toast.success('Window closed — impact report frozen');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to close');
    } finally { setBusy(false); }
  };

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`reopen-row-${r.id}`}>
      <td className="px-4 py-2 font-mono text-slate-700">{r.period_name}</td>
      <td className="px-4 py-2 text-xs text-slate-500">
        {REOPEN_TYPES.find((t) => t.id === r.reopen_type)?.label || r.reopen_type}
      </td>
      <td className="px-4 py-2">{r.requested_by_name || r.requested_by || '—'}</td>
      <td className="px-4 py-2">{r.approved_by_name || (r.status === 'pending' ? <span className="text-amber-600 text-xs">awaiting…</span> : '—')}</td>
      <td className="px-4 py-2 text-right text-xs text-slate-500">
        {r.duration_minutes}m {remaining && <span className="ml-1 font-mono text-emerald-700">· {remaining}</span>}
      </td>
      <td className="px-4 py-2">
        <span className={`text-[10px] font-semibold uppercase border rounded-full px-2 py-0.5 ${STATUS_BADGE[r.status] || 'bg-slate-100'}`}>
          {r.status}
        </span>
      </td>
      <td className="px-4 py-2 text-right font-mono">{r.affected_journals_count ?? 0}</td>
      <td className="px-4 py-2 text-right">
        <div className="inline-flex gap-1">
          {r.status === 'pending' && !isOwnRow && (
            <>
              <button onClick={approve} disabled={busy}
                      data-testid={`reopen-approve-${r.id}`}
                      className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded">
                <CheckCircle2 size={12} className="inline -mt-0.5 mr-0.5"/> Approve
              </button>
              <button onClick={reject} disabled={busy}
                      data-testid={`reopen-reject-${r.id}`}
                      className="text-xs bg-rose-100 hover:bg-rose-200 text-rose-800 px-2 py-1 rounded">
                <XCircle size={12} className="inline -mt-0.5 mr-0.5"/> Reject
              </button>
            </>
          )}
          {r.status === 'pending' && isOwnRow && (
            <span className="text-[10px] text-slate-400 italic">awaiting Four-Eyes approver</span>
          )}
          {r.status === 'approved' && (
            <button onClick={closeNow} disabled={busy}
                    data-testid={`reopen-close-${r.id}`}
                    className="text-xs bg-slate-900 hover:bg-slate-700 text-white px-2 py-1 rounded">
              <Lock size={12} className="inline -mt-0.5 mr-0.5"/> Close Now
            </button>
          )}
          <button onClick={onDetail}
                  data-testid={`reopen-detail-${r.id}`}
                  className="text-xs text-indigo-600 hover:underline px-2 py-1">
            <FileText size={12} className="inline -mt-0.5 mr-0.5"/> Detail
          </button>
        </div>
      </td>
    </tr>
  );
}


// ───────────────────────────────────────────────────────────────────────
// Create modal
// ───────────────────────────────────────────────────────────────────────
function CreateModal({ periods, onClose, onCreated }) {
  const [periodName, setPeriodName] = useState(periods[0]?.name || '');
  const [type, setType] = useState('missing_invoice');
  const [duration, setDuration] = useState(120);
  const [reason, setReason] = useState('');
  const [ext, setExt] = useState('');
  const [busy, setBusy] = useState(false);

  const extRequired = duration > 120;

  const submit = async () => {
    if (reason.trim().length < 20) {
      toast.error('Reason must be at least 20 characters');
      return;
    }
    if (extRequired && ext.trim().length < 10) {
      toast.error('Extended justification required for durations > 120 minutes');
      return;
    }
    setBusy(true);
    try {
      await axiosInstance.post('/period-reopen/request', {
        period_name: periodName,
        reopen_type: type,
        duration_minutes: Number(duration),
        reason: reason.trim(),
        extended_justification: ext.trim() || null,
      });
      toast.success('Request submitted — awaiting Four-Eyes approver');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit request');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
         data-testid="reopen-create-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">Request Period Reopen</h2>
        <p className="text-xs text-slate-500 mb-4">
          The approver will be a different Admin (Four-Eyes Principle).
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Closed Period</label>
            <select value={periodName} onChange={(e) => setPeriodName(e.target.value)}
                    data-testid="reopen-period-select"
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1">
              {periods.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Reopen Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}
                    data-testid="reopen-type-select"
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1">
              {REOPEN_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">
              Duration (minutes) — default 120, max 480
            </label>
            <input type="number" value={duration} min={15} max={480}
                   onChange={(e) => setDuration(e.target.value)}
                   data-testid="reopen-duration-input"
                   className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1"/>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">
              Reason (min 20 chars) <span className="text-rose-600">*</span>
            </label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      data-testid="reopen-reason-input"
                      rows={3}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1"
                      placeholder="What needs to be posted, and why couldn't it be added before close?"/>
          </div>
          {extRequired && (
            <div>
              <label className="text-xs font-semibold text-slate-600">
                Extended Justification (required for &gt; 120 minutes)
                <span className="text-rose-600"> *</span>
              </label>
              <textarea value={ext} onChange={(e) => setExt(e.target.value)}
                        data-testid="reopen-ext-input"
                        rows={2}
                        className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1"/>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose}
                  className="text-sm px-3 py-1.5 border border-slate-300 rounded">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="reopen-submit-btn"
                  className="text-sm bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded disabled:opacity-50">
            {busy ? 'Submitting…' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// Detail modal — including Reopen Impact Report + Differential Report
// ───────────────────────────────────────────────────────────────────────
function DetailModal({ row, onClose }) {
  const [diff, setDiff] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const ir = row.impact_report;

  const loadDifferential = async () => {
    setLoadingDiff(true);
    try {
      const r = await axiosInstance.get(`/period-reopen/${row.id}/differential-report`);
      setDiff(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to build differential report');
    } finally {
      setLoadingDiff(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
         data-testid="reopen-detail-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">
          Reopen Request · <span className="font-mono">{row.period_name}</span>
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          <span className={`text-[10px] font-semibold uppercase border rounded-full px-2 py-0.5 mr-2 ${STATUS_BADGE[row.status]}`}>
            {row.status}
          </span>
          {row.reopen_type}
        </p>
        <Section title="Reason"            body={row.reason}/>
        {row.extended_justification &&
          <Section title="Extended Justification" body={row.extended_justification}/>
        }
        <div className="grid grid-cols-2 gap-3 text-xs my-3">
          <Field label="Requested By"     value={`${row.requested_by_name || ''} · ${row.requested_at?.slice(0, 19) || ''}`}/>
          <Field label="Approved By"      value={row.approved_by_name ? `${row.approved_by_name} · ${row.approved_at?.slice(0, 19) || ''}` : '—'}/>
          {row.rejected_by_name &&
            <Field label="Rejected By"    value={`${row.rejected_by_name} · ${row.rejected_at?.slice(0, 19) || ''}`}/>}
          {row.rejection_reason &&
            <Field label="Rejection Reason" value={row.rejection_reason} span={2}/>}
          <Field label="Duration"         value={`${row.duration_minutes}m`}/>
          <Field label="Expires At"       value={row.expires_at?.slice(0, 19) || '—'}/>
          {row.closed_again_at &&
            <Field label="Closed Again At" value={`${row.closed_again_at.slice(0, 19)} by ${row.closed_again_by_name || ''}`} span={2}/>}
        </div>

        {/* Impact Report */}
        <div className="border-t border-slate-200 pt-3 mt-3">
          <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-600"/>
            Reopen Impact Report
          </h3>
          {!ir && (
            <p className="text-xs text-slate-400 italic">
              Available after the window closes. So far: {row.affected_journals_count ?? 0} journals
              touched during the window.
            </p>
          )}
          {ir && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <Stat label="Journals Added"  value={ir.journals_added}/>
              <Stat label="Invoices Added"  value={ir.invoices_added}/>
              <Stat label="Expenses Added"  value={ir.expenses_added}/>
              <Stat label="Payments Added"  value={ir.payments_added}/>
              <Stat label="Bills Added"     value={ir.bills_added}/>
              <Stat label="Users Active"    value={ir.users_active?.length || 0}/>
              <Stat label="Total Debit"     value={`${SAR(ir.total_debit)} SAR`}  wide/>
              <Stat label="Total Credit"    value={`${SAR(ir.total_credit)} SAR`} wide/>
              <Stat label="Built At"        value={ir.built_at?.slice(0, 19)}     wide/>
              {ir.user_names?.length > 0 && (
                <div className="col-span-3 text-slate-500">
                  <Users size={12} className="inline -mt-0.5 mr-1"/>
                  {ir.user_names.join(', ')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Differential Report (Phase 8.2 — audit-grade truth diff) */}
        <div className="border-t border-slate-200 pt-3 mt-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <FileText size={14} className="text-indigo-600"/>
              Differential Report
              <span className="text-[10px] font-normal text-slate-400">· Trial Balance / P&L / BS / VAT delta</span>
            </h3>
            <button
              onClick={loadDifferential}
              disabled={loadingDiff}
              data-testid={`reopen-diff-btn-${row.id}`}
              className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded disabled:opacity-50"
            >
              {loadingDiff ? 'Building…' : (diff ? 'Rebuild' : 'Build Report')}
            </button>
          </div>
          {!diff && !loadingDiff && (
            <p className="text-xs text-slate-400 italic">
              Click &quot;Build Report&quot; — generates a deterministic, hashable
              snapshot comparing book state before/after the window.
            </p>
          )}
          {diff && <DifferentialView diff={diff}/>}
        </div>

        <div className="flex justify-end mt-5">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-slate-300 rounded">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


function DifferentialView({ diff }) {
  const riskColor = {
    LOW:    'bg-emerald-100 text-emerald-800 border-emerald-300',
    MEDIUM: 'bg-amber-100 text-amber-800 border-amber-300',
    HIGH:   'bg-rose-100 text-rose-800 border-rose-300',
  }[diff.risk?.level] || 'bg-slate-100 text-slate-700';

  return (
    <div className="text-xs space-y-3" data-testid="differential-view">
      {/* Risk + Hash */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-semibold uppercase border rounded-full px-2 py-0.5 ${riskColor}`}>
          Risk · {diff.risk?.level}
        </span>
        <span className="text-slate-500">
          max swing: {(diff.risk?.max_account_pct_change * 100 || 0).toFixed(2)}%
        </span>
      </div>
      {diff.risk?.rationale && (
        <p className="text-[11px] text-slate-600">{diff.risk.rationale}</p>
      )}

      {/* P&L + BS Delta */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <Stat label="Revenue Δ"      value={`${SAR(diff.pnl_delta?.revenue_delta)} SAR`} wide/>
        <Stat label="Expenses Δ"     value={`${SAR(diff.pnl_delta?.expenses_delta)} SAR`} wide/>
        <Stat label="Net Income Δ"   value={`${SAR(diff.pnl_delta?.net_income_delta)} SAR`} wide/>
        <Stat label="Assets Δ"       value={`${SAR(diff.balance_sheet_delta?.assets_delta)} SAR`} wide/>
        <Stat label="Liabilities Δ"  value={`${SAR(diff.balance_sheet_delta?.liabilities_delta)} SAR`} wide/>
        <Stat label="Equity Δ"       value={`${SAR(diff.balance_sheet_delta?.equity_delta)} SAR`} wide/>
      </div>
      {diff.balance_sheet_delta && !diff.balance_sheet_delta.is_balanced && (
        <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1">
          ⚠ Balance sheet discrepancy: {SAR(diff.balance_sheet_delta.discrepancy)} SAR — review the affected journals for incomplete double-entry.
        </div>
      )}

      {/* Trial Balance deltas table */}
      {(diff.trial_balance?.delta || []).length > 0 && (
        <div className="border border-slate-100 rounded">
          <div className="bg-slate-50 px-2 py-1 text-[10px] uppercase text-slate-500 font-semibold">
            Account Deltas ({diff.trial_balance.delta.length})
          </div>
          <table className="w-full text-[11px]">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left px-2 py-1">Account</th>
                <th className="text-right px-2 py-1">Before</th>
                <th className="text-right px-2 py-1">After</th>
                <th className="text-right px-2 py-1">Δ</th>
                <th className="text-right px-2 py-1">%</th>
              </tr>
            </thead>
            <tbody>
              {diff.trial_balance.delta.slice(0, 20).map((r) => (
                <tr key={r.account_id} className="border-t border-slate-100">
                  <td className="px-2 py-1 font-mono text-[10px] text-slate-600">
                    {r.account_number} · {r.account_name}
                  </td>
                  <td className="px-2 py-1 text-right">{SAR(r.before_balance)}</td>
                  <td className="px-2 py-1 text-right">{SAR(r.after_balance)}</td>
                  <td className={`px-2 py-1 text-right font-semibold ${
                    r.balance_delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {r.balance_delta >= 0 ? '+' : ''}{SAR(r.balance_delta)}
                  </td>
                  <td className="px-2 py-1 text-right text-slate-500">
                    {(r.pct_change * 100).toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VAT box deltas */}
      <div className="grid grid-cols-4 gap-2">
        <Stat label="Box 13 Δ"  value={`${SAR(diff.vat_impact?.box_13_delta)} SAR`}/>
        <Stat label="Box 14 Δ"  value={`${SAR(diff.vat_impact?.box_14_delta)} SAR`}/>
        <Stat label="Box 15 Δ"  value={`${SAR(diff.vat_impact?.box_15_delta)} SAR`}/>
        <Stat label="Net VAT Δ" value={`${SAR(diff.vat_impact?.net_vat_change)} SAR`}/>
      </div>

      {/* V2 — Reopen Activity Summary (Added + Modified + Voided) */}
      <ReopenActivitySummary diff={diff}/>

      {/* Footer — auditor signature line */}
      <div className="border-t border-slate-100 pt-2 text-[10px] text-slate-400 font-mono break-all">
        Hash: {diff.footer?.snapshot_hash}
        <br/>
        Built at {diff.footer?.generated_at} · engine v{diff.footer?.engine_version}
      </div>
    </div>
  );
}

function Section({ title, body }) {
  return (
    <div className="text-xs mb-2">
      <div className="font-semibold text-slate-600 mb-0.5">{title}</div>
      <div className="text-slate-800 bg-slate-50 border border-slate-100 rounded px-2 py-1.5 whitespace-pre-wrap">
        {body || '—'}
      </div>
    </div>
  );
}
function Field({ label, value, span }) {
  return (
    <div className={`text-xs ${span === 2 ? 'col-span-2' : ''}`}>
      <div className="text-slate-500">{label}</div>
      <div className="text-slate-900 font-medium">{value || '—'}</div>
    </div>
  );
}
function Stat({ label, value, wide }) {
  return (
    <div className={`bg-slate-50 border border-slate-100 rounded p-2 ${wide ? '' : ''}`}>
      <div className="text-[10px] uppercase text-slate-500 tracking-wide">{label}</div>
      <div className="text-sm font-bold text-slate-900 font-mono">{value ?? 0}</div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// V2 — Reopen Activity Summary (Added / Modified / Voided)
// ───────────────────────────────────────────────────────────────────────
function ReopenActivitySummary({ diff }) {
  const added    = diff?.journal_diff?.added    || [];
  const modified = diff?.journal_diff?.modified || [];
  const voided   = diff?.journal_diff?.voided   || [];
  const uniqueUsers = new Set([
    ...added.map((r) => r.created_by).filter(Boolean),
    ...modified.map((r) => r.amended_by).filter(Boolean),
    ...voided.map((r) => r.user).filter(Boolean),
  ]).size;

  return (
    <div className="border border-slate-100 rounded" data-testid="reopen-activity-summary">
      <div className="bg-slate-50 px-2 py-1 text-[10px] uppercase text-slate-500 font-semibold">
        Reopen Activity Summary
      </div>
      <div className="grid grid-cols-4 gap-2 p-2">
        <Stat label="Added"         value={added.length}/>
        <Stat label="Modified"      value={modified.length}/>
        <Stat label="Voided"        value={voided.length}/>
        <Stat label="Unique Users"  value={uniqueUsers}/>
      </div>

      {modified.length > 0 && (
        <div className="border-t border-slate-100">
          <div className="px-2 py-1 text-[10px] uppercase text-amber-700 font-semibold bg-amber-50">
            Modified ({modified.length})
          </div>
          <table className="w-full text-[11px]">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left px-2 py-1">Journal</th>
                <th className="text-center px-2 py-1">v#</th>
                <th className="text-left px-2 py-1">By</th>
                <th className="text-left px-2 py-1">At</th>
                <th className="text-left px-2 py-1">Reason</th>
              </tr>
            </thead>
            <tbody>
              {modified.map((m) => (
                <tr key={m.amendment_id} className="border-t border-slate-100">
                  <td className="px-2 py-1 font-mono text-[10px] text-slate-600">{m.journal_id?.slice(0, 14)}…</td>
                  <td className="px-2 py-1 text-center">{m.version_number}</td>
                  <td className="px-2 py-1">{m.amended_by || '—'}</td>
                  <td className="px-2 py-1 text-slate-500">{m.amended_at?.slice(0, 19)}</td>
                  <td className="px-2 py-1 text-slate-700 truncate max-w-[200px]" title={m.reason}>
                    {m.reason || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {voided.length > 0 && (
        <div className="border-t border-slate-100">
          <div className="px-2 py-1 text-[10px] uppercase text-rose-700 font-semibold bg-rose-50">
            Voided / Superseded ({voided.length})
          </div>
          <table className="w-full text-[11px]">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left px-2 py-1">Journal</th>
                <th className="text-center px-2 py-1">Old v#</th>
                <th className="text-center px-2 py-1">By v#</th>
                <th className="text-left px-2 py-1">User</th>
                <th className="text-left px-2 py-1">At</th>
              </tr>
            </thead>
            <tbody>
              {voided.map((v) => (
                <tr key={`${v.journal_id}-${v.version_number}`} className="border-t border-slate-100">
                  <td className="px-2 py-1 font-mono text-[10px] text-slate-600">{v.journal_id?.slice(0, 14)}…</td>
                  <td className="px-2 py-1 text-center text-rose-700">{v.version_number}</td>
                  <td className="px-2 py-1 text-center text-emerald-700">{v.superseded_by_version}</td>
                  <td className="px-2 py-1">{v.user || '—'}</td>
                  <td className="px-2 py-1 text-slate-500">{v.superseded_at?.slice(0, 19)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
